import React from 'react';
import { Target, Rocket, Wrench, BookOpen, Users, Lightbulb, MapPin, Link2, Mail } from 'lucide-react';
import PolicySectionCard from '@/components/policy/PolicySectionCard';
import PolicyLayout from '@/components/policy/PolicyLayout';
import PageHeader from '@/components/layout/PageHeader';
import { motion } from 'framer-motion';

const AboutUs = () => {
  // Define all variants consistently
  const cardVariants = [
    "blue", "purple", "teal", "rose", 
    "amber", "indigo", "cyan", "emerald"
  ] as const;
  
  return (
    <PolicyLayout>
      <PageHeader
        title="About Us | Edaptica"
        description="Empowering Every Learner with Artificial Intelligence"
        icon={<Users className="w-8 h-8 text-primary" />}
      />

      <motion.p 
        className="mt-6 text-lg max-w-3xl mx-auto text-gray-700 mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        Edaptica is an AI-powered adaptive learning platform built to personalize the educational journey of students, professionals, and lifelong learners. Backed by the innovation of Cibeto Technologies Pvt. Ltd., Edaptica combines cognitive science, cutting-edge technology, and deep data analytics to transform the way people learn and grow.
      </motion.p>

      {/* Cards Grid */}
      <div className="space-y-6">
        <PolicySectionCard
          title="Our Mission"
          icon={Target}
          variant="blue"
        >
          <p>To make learning more personalized, engaging, and effective by using AI-driven tools and neuroscience-backed methodologies. We aim to equip every learner with the right content at the right time, based on their pace, performance, and goals.</p>
        </PolicySectionCard>

        <PolicySectionCard
          title="Our Vision"
          icon={Rocket}
          variant="purple"
        >
          <p>To become the go-to adaptive learning ecosystem for competitive exams, professional certifications, and skill-building—where learners thrive with real-time support, insightful analytics, and meaningful progress.</p>
        </PolicySectionCard>

        <PolicySectionCard
          title="What We Offer"
          icon={Wrench}
          variant="teal"
        >
          <ul className="list-disc pl-6 space-y-2">
            <li>Adaptive Learning Engine – Personalized pathways that evolve with you</li>
            <li>Smart Mock Tests – Dynamic question sets based on your strengths and weaknesses</li>
            <li>Progress Dashboard – Visual analytics to track growth and mastery</li>
            <li>AI-Suggested Resources – Curated study materials, videos, and notes</li>
            <li>Gamified Experience – Earn badges, streaks, and rewards as you learn</li>
            <li>Multilingual Support – Learn in your preferred language</li>
            <li>E-Book & Notes Access – Download interactive study notes powered by Edaptica</li>
          </ul>
        </PolicySectionCard>

        <PolicySectionCard
          title="Our Story"
          icon={BookOpen}
          variant="rose"
        >
          <div className="space-y-4">
            <p>Founded in 2025 under the parent brand Cibeto Technologies Pvt. Ltd., Edaptica was born out of a simple question—</p>
            <p className="italic">"What if learning could adapt to you, instead of you adapting to learning?"</p>
            <p>With this vision, a team of educators, engineers, and AI experts created a platform that understands each learner's needs and delivers education in a way that feels natural, focused, and motivating.</p>
            <p>Today, Edaptica is helping thousands of users across India and beyond—whether they're preparing for JEE, NEET, UPSC, or upskilling for data science, AI, and professional roles.</p>
          </div>
        </PolicySectionCard>

        <PolicySectionCard
          title="Leadership"
          icon={Users}
          variant="amber"
        >
          <div className="space-y-2">
            <p>Edaptica is a product of Cibeto Technologies Pvt. Ltd.</p>
            <p>Learn more: <a href="http://www.cibeto.co.in" className="text-gray-800 underline">www.cibeto.co.in</a></p>
            <p>We are passionate about using technology for good—and education is where our heart is.</p>
          </div>
        </PolicySectionCard>

        <PolicySectionCard
          title="Core Values"
          icon={Lightbulb}
          variant="indigo"
        >
          <ul className="list-disc pl-6 space-y-2">
            <li>Personalization First – Every learner is unique, and so should be their learning path</li>
            <li>Transparency – No hidden tricks, just data-driven clarity</li>
            <li>Innovation with Purpose – Tech that serves real needs, not just trends</li>
            <li>Accessibility for All – Learning should be inclusive, affordable, and flexible</li>
          </ul>
        </PolicySectionCard>

        <PolicySectionCard
          title="Join the Edaptica Community"
          icon={MapPin}
          variant="cyan"
        >
          <div className="space-y-4">
            <p>Whether you're a student, teacher, parent, or lifelong learner—Edaptica is your partner in progress.</p>
            <p>Explore our platform, take your first adaptive test, and discover the smarter way to learn.</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <a href="/auth" className="inline-flex items-center gap-2 bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-lg transition-colors">
                <Link2 className="h-5 w-5" />
                Start Learning Now
              </a>
              <a href="mailto:support@edaptica.com" className="inline-flex items-center gap-2 bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-lg transition-colors">
                <Mail className="h-5 w-5" />
                support@edaptica.com
              </a>
            </div>
          </div>
        </PolicySectionCard>
      </div>

      {/* Footer */}
      <footer className="text-center text-sm text-muted-foreground pt-8 mt-8 border-t border-gray-100">
        © {new Date().getFullYear()} Cibeto Technologies Private Limited. All rights reserved.
      </footer>
    </PolicyLayout>
  );
};

export default AboutUs;
